import type { AxiosError } from 'axios';
import { Button, Center, Container, Flex, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Department, Team, UserResponse } from '@/api/generated/models';
import {
  getListDepartmentsQueryKey,
  useDeleteDepartment,
  useGetDepartment,
  useListTeams
} from '@/api/generated/departments-teams/departments-teams';
import { useListUsers } from '@/api/generated/users/users';
import {
  BackButton,
  ConfirmActionModal,
  DetailStatBox,
  MobileActionBar,
  StatusBanner,
  StatusPill,
  tableRowStyle
} from '@/components/common';
import { DepartmentFormModal } from '@/features/departments/components/DepartmentFormModal';
import { TeamActionsMenu } from '@/features/departments/components/TeamActionsMenu';
import { TeamFormModal } from '@/features/departments/components/TeamFormModal';
import { successNotificationIcon } from '@/theme/notificationIcons';
import { useSavedMessage } from '@/utils/useSavedMessage';

const departmentErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

export const DepartmentDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { departmentId } = useParams<{ departmentId: string }>();
  const id = departmentId ?? '';

  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [teamModalOpened, setTeamModalOpened] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | undefined>(undefined);
  const [savedMessage, setSavedMessage] = useSavedMessage();

  const { data, isLoading } = useGetDepartment(id, { query: { enabled: !!id } });
  // GET /departments/{id} vraća ResponseEntity<?> na backendu (isto kao
  // ObjectController) — springdoc ne zna deklarirati oblik, pa se ovdje
  // ručno tipizira, vidi ObjectDetailPage.
  const department = data as Department | undefined;

  const { data: teamsData, isLoading: teamsLoading } = useListTeams(id, {
    query: { enabled: !!id }
  });
  const teams: Team[] = teamsData ?? [];

  const { data: usersData } = useListUsers(undefined, { query: { refetchOnMount: 'always' } });
  const users: UserResponse[] = usersData ?? [];
  const memberCountByTeam = useMemo(() => {
    const map = new Map<string, number>();
    users.forEach((user) => {
      if (!user.teamId) return;
      map.set(user.teamId, (map.get(user.teamId) ?? 0) + 1);
    });
    return map;
  }, [users]);

  const totalMembers = teams.reduce(
    (sum, team) => sum + (team.id ? (memberCountByTeam.get(team.id) ?? 0) : 0),
    0
  );

  const deleteMutation = useDeleteDepartment({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListDepartmentsQueryKey() });
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('departments.deleteSuccess')
        });
        navigate('/users/departments');
      },
      onError: (error) => {
        notifications.show({
          color: 'red',
          message: departmentErrorMessage(error, t('common.error'))
        });
        setDeleteModalOpened(false);
      }
    }
  });

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!department) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('departments.detailNotAvailable')}
        </Text>
      </Container>
    );
  }

  const openNewTeam = () => {
    setEditingTeam(undefined);
    setTeamModalOpened(true);
  };

  const openEditTeam = (team: Team) => {
    setEditingTeam(team);
    setTeamModalOpened(true);
  };

  const headerActions = (
    <>
      <Button variant="outline" color="red" onClick={() => setDeleteModalOpened(true)}>
        {t('departments.deleteAction')}
      </Button>
      <Button variant="outline" onClick={() => setEditModalOpened(true)}>
        {t('departments.editDepartment')}
      </Button>
      <Button onClick={openNewTeam}>+ {t('departments.team.newTeam')}</Button>
    </>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/users/departments" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', sm: 'flex-start' }}
          gap={16}
        >
          <Stack gap={4}>
            <Text fz={24} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {department.name}
            </Text>
            {department.description && (
              <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
                {department.description}
              </Text>
            )}
          </Stack>
          <Group gap={12} visibleFrom="sm">
            {headerActions}
          </Group>
          <MobileActionBar>{headerActions}</MobileActionBar>
        </Flex>

        <Group gap={16} wrap="wrap" align="stretch">
          <DetailStatBox label={t('departments.colTeams')} value={String(teams.length)} />
          <DetailStatBox label={t('departments.colMembers')} value={String(totalMembers)} />
        </Group>

        <Stack gap={0}>
          <Text fz={16} fw={700} pb={12} style={{ color: 'var(--color-text-primary)' }}>
            {t('departments.teamsSectionTitle')}
          </Text>

          {!teamsLoading && teams.length === 0 && (
            <Center py={32}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('departments.team.emptyState')}
              </Text>
            </Center>
          )}

          {teams.length > 0 && (
            <Stack
              gap={0}
              style={{
                border: '1px solid var(--color-border-default)',
                borderRadius: 12,
                backgroundColor: 'var(--color-bg-surface)',
                overflow: 'hidden'
              }}
            >
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                <Text
                  fz={10}
                  fw={600}
                  w={220}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.team.colName')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={180}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.colMembers')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={60}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.team.colColour')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={140}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.colAction')}
                </Text>
              </div>

              {teams.map((team, index) => (
                <div
                  key={team.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/users/departments/${id}/teams/${team.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      navigate(`/users/departments/${id}/teams/${team.id}`);
                    }
                  }}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                    cursor: 'pointer'
                  }}
                >
                  <Group gap={8} w={220} wrap="nowrap">
                    <Text
                      fz={13.5}
                      fw={600}
                      style={{ color: 'var(--color-text-primary)' }}
                      truncate
                    >
                      {team.name}
                    </Text>
                    {team.isActive === false && (
                      <StatusPill
                        label={t('departments.team.inactiveBadge')}
                        bg="var(--color-status-draft-soft)"
                        fg="var(--color-status-draft)"
                      />
                    )}
                  </Group>
                  <Text
                    fz={13}
                    w={180}
                    style={{
                      color: 'var(--color-text-secondary)',
                      fontFamily: 'var(--mantine-font-family-monospace)'
                    }}
                  >
                    {team.id ? (memberCountByTeam.get(team.id) ?? 0) : 0}
                  </Text>
                  <Group w={60}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: team.colour ?? 'var(--color-border-default)'
                      }}
                    />
                  </Group>
                  <Group w={140} justify="flex-end">
                    <TeamActionsMenu
                      departmentId={id}
                      team={team}
                      onEdit={() => openEditTeam(team)}
                    />
                  </Group>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <DepartmentFormModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        department={department}
        onSaved={setSavedMessage}
      />

      <TeamFormModal
        opened={teamModalOpened}
        onClose={() => setTeamModalOpened(false)}
        defaultDepartmentId={id}
        team={editingTeam}
        onSaved={setSavedMessage}
      />

      <ConfirmActionModal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={() => deleteMutation.mutate({ id })}
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
        danger
        title={t('departments.deleteTitle')}
        message={t('departments.deleteMessage', { name: department.name })}
        confirmLabel={t('departments.deleteConfirm')}
      />
    </Container>
  );
};
